import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, PhoneCall, DoorClosed, Activity as ActivityIcon, Volume2, VolumeX, Radio } from "lucide-react";
import { MorningBriefing } from "@/components/MorningBriefing";
import { VoiceCall } from "@/components/VoiceCall";
import { FrontDesk } from "@/components/FrontDesk";
import { useStore } from "@/lib/store";
import { DEPARTMENTS } from "@/data/seed";
import { unlockAudio, startAmbience, stopAmbience } from "@/lib/sfx";
import { prewarmToken, employeeInstructions, EMPLOYEE_VOICE } from "@/lib/realtime";
import { twilio } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Pretty-print a US/E.164 number, e.g. +14707651212 -> +1 (470) 765-1212
function fmtPhone(n) {
  if (!n) return "";
  const d = String(n).replace(/[^\d]/g, "");
  if (d.length === 11 && d[0] === "1") return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return n;
}

// Simplified status model for the office boxes:
//   green  = On duty   (present / available)
//   red    = On a task (actively working, collaborating, escalated, waiting)
//   gray   = Off duty
const OFFICE = {
  on:   { key: "on",   label: "On duty",   color: "#22C55E" },
  task: { key: "task", label: "On a task", color: "#FF3B5C" },
  call: { key: "call", label: "On a call", color: "#FF3B5C" },
  off:  { key: "off",  label: "Off duty",  color: "#64748B" },
};

function officeState(status) {
  if (status === "off_duty") return OFFICE.off;
  if (status === "on_call") return OFFICE.call;
  if (status === "available") return OFFICE.on;
  return OFFICE.task; // working, collaborating, waiting_*, escalation
}

// Live-ticking mm:ss timer for how long an associate has been on the current call.
function CallTimer({ startedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const start = startedAt ? new Date(startedAt).getTime() : now;
  let total = Math.max(0, Math.floor((now - start) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return <span className="font-mono tabular-nums">{`${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`}</span>;
}

// A single associate's office — click anywhere to start talking to them.
function Office({ employee, status, onTalk, phoneNumber, callStartedAt }) {
  const st = officeState(status);
  const busy = st.key === "task" || st.key === "call";
  const onCall = st.key === "call";
  // Pre-warm the connection on hover so the greeting starts sooner on click.
  const warm = () => prewarmToken(employeeInstructions(employee), EMPLOYEE_VOICE[employee.id] || "marin");
  return (
    <div className="group relative w-48 shrink-0" onMouseEnter={warm} onFocus={warm}>
      <motion.button
        layout
        onClick={() => onTalk(employee.id)}
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.985 }}
        className="relative flex h-[248px] w-full flex-col items-center overflow-hidden rounded-2xl border bg-[#0A1326]/70 p-4 text-center backdrop-blur-md"
        style={{ borderColor: `${st.color}66`, boxShadow: `0 0 0 1px ${st.color}22, 0 0 26px ${st.color}22`, transition: "border-color .5s, box-shadow .5s" }}
        data-testid={`office-${employee.id}`}
        title={`Talk to ${employee.name}`}
        aria-label={`Talk to ${employee.name}, ${st.label}`}
      >
        {/* status wash at the top of the office */}
        <span className="pointer-events-none absolute inset-x-0 top-0 h-20" style={{ background: `radial-gradient(120% 80% at 50% 0%, ${st.color}22, transparent 70%)`, transition: "background .5s" }} />

        {/* "thinking" shimmer sweep while mid-task */}
        {busy && (
          <motion.span
            className="pointer-events-none absolute -inset-y-4 w-1/3"
            style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent)" }}
            initial={{ left: "-40%" }}
            animate={{ left: "130%" }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            data-testid={`office-shimmer-${employee.id}`}
          />
        )}

        <div className="relative">
          <div className="h-20 w-20 overflow-hidden rounded-2xl" style={{ border: `2px solid ${st.color}`, boxShadow: `0 0 18px ${st.color}66`, transition: "border-color .5s, box-shadow .5s" }}>
            <img
              src={employee.avatar}
              alt={employee.name}
              className="h-full w-full object-cover transition-[filter,opacity] duration-500"
              style={onCall ? { filter: "grayscale(1)", opacity: 0.5 } : undefined}
              loading="lazy"
            />
          </div>
          <span className="pointer-events-none absolute -bottom-1.5 -right-1.5 grid h-6 w-6 place-items-center rounded-full bg-[#00E5FF] text-[#030A16] opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
            <Phone size={12} />
          </span>
          {onCall && (
            <motion.span
              className="pointer-events-none absolute -top-1.5 -right-1.5 grid h-7 w-7 place-items-center rounded-full bg-[#FF3B5C] text-white shadow-lg"
              style={{ boxShadow: "0 0 14px #FF3B5C" }}
              animate={{ scale: [1, 1.12, 1] }}
              transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
              data-testid={`office-call-badge-${employee.id}`}
              title="On a call"
            >
              <PhoneCall size={13} />
            </motion.span>
          )}
        </div>

        <div className={cn("mt-3 font-display text-base font-semibold transition-colors duration-500", onCall ? "text-slate-500" : "text-white")}>{employee.name}</div>
        <div className={cn("text-xs transition-colors duration-500", onCall ? "text-slate-600" : "text-slate-400")}>{employee.title}</div>

        {phoneNumber && (
          <span
            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#00E5FF]/35 bg-[#00E5FF]/10 px-2.5 py-1 font-mono text-[11px] text-[#00E5FF]"
            title="Main business line — call or text this number"
            data-testid={`office-phone-${employee.id}`}
          >
            <Phone size={11} /> {fmtPhone(phoneNumber)}
          </span>
        )}

        <span
          className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium"
          style={{ background: `${st.color}1A`, color: st.color, border: `1px solid ${st.color}55`, transition: "all .5s" }}
          data-testid={`office-status-${employee.id}`}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: st.color, boxShadow: `0 0 8px ${st.color}` }} />
          {onCall && <PhoneCall size={12} />}
          {onCall ? "Busy on a call" : st.label}
          {onCall && <><span className="opacity-60">·</span><CallTimer startedAt={callStartedAt} /></>}
          {busy && !onCall && (
            <span className="ml-0.5 inline-flex items-center gap-0.5">
              {[0, 1, 2].map((i) => (
                <motion.span key={i} className="h-1 w-1 rounded-full" style={{ background: st.color }} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }} />
              ))}
            </span>
          )}
        </span>
      </motion.button>
    </div>
  );
}

// A department "wing" grouping the offices of associates who share a department.
// Every wing is boxed uniformly so single- and multi-associate departments match,
// and every office is the same width with equal spacing. The department name and
// its live "active / open" stats sit left-aligned above the wing box.
function DepartmentWing({ department, employees, statusOf, onTalk, phoneByEmployee, callStarts }) {
  const onTask = employees.filter((e) => ["task", "call"].includes(officeState(statusOf(e)).key)).length;
  const openTasks = employees.reduce((sum, e) => sum + (e.openAssignments || 0), 0);
  return (
    <section className="shrink-0" data-testid={`wing-${department.id}`}>
      <div className="mb-3">
        <div className="flex items-center justify-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: department.color, boxShadow: `0 0 8px ${department.color}` }} />
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-slate-200">{department.name}</h2>
        </div>
        <div className="mt-2 flex items-center justify-center gap-2 text-xs font-medium" data-testid={`wing-stat-${department.id}`}>
          <span
            className="flex items-center gap-1 rounded-full border px-2 py-0.5"
            style={{
              color: onTask ? OFFICE.task.color : "#94A3B8",
              borderColor: onTask ? `${OFFICE.task.color}55` : "rgba(255,255,255,0.12)",
              background: onTask ? `${OFFICE.task.color}14` : "rgba(255,255,255,0.04)",
            }}
          >
            <ActivityIcon size={12} /> <b className="font-mono">{onTask}</b> active
          </span>
          <span className="flex items-center gap-1 rounded-full border border-white/12 bg-white/5 px-2 py-0.5 text-slate-200">
            <b className="font-mono">{openTasks}</b> open
          </span>
        </div>
      </div>
      <div
        className="flex gap-4 rounded-3xl border border-dashed p-4"
        style={{ borderColor: `${department.color}55`, background: `${department.color}0A` }}
      >
        {employees.map((e) => (
          <Office key={e.id} employee={e} status={statusOf(e)} onTalk={onTalk} phoneNumber={(phoneByEmployee || {})[e.id]} callStartedAt={(callStarts || {})[e.id]} />
        ))}
      </div>
    </section>
  );
}

export default function Headquarters() {
  const { state } = useStore();
  const [briefing, setBriefing] = useState(false);
  const [callId, setCallId] = useState(null);
  const [callQuery, setCallQuery] = useState(null); // question captured at the front desk
  const [live, setLive] = useState({}); // transient status overrides (live sync sim)
  const [noraPhone, setNoraPhone] = useState("");
  const [onCall, setOnCall] = useState([]); // associate ids currently on a phone call
  const [callStarts, setCallStarts] = useState({}); // associate id -> call started_at ISO
  useEffect(() => {
    twilio.status().then((s) => { if (s.configured && s.phone_number) setNoraPhone(s.phone_number); }).catch(() => {});
  }, []);
  useEffect(() => {
    let alive = true;
    const poll = () => twilio.active().then((d) => {
      if (!alive) return;
      setOnCall(d.on_call || []);
      const starts = {};
      (d.calls || []).forEach((c) => { starts[c.associate] = c.started_at; });
      setCallStarts(starts);
    }).catch(() => {});
    poll();
    const id = setInterval(poll, 2000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("iq-floor-sounds") === "1"; } catch (_) { return false; }
  });
  // Voice "front desk": listens for an associate's name and connects you.
  const [fdOn, setFdOn] = useState(() => { try { return sessionStorage.getItem("iq-frontdesk") === "on"; } catch (_) { return false; } });
  const [promptSeen, setPromptSeen] = useState(() => { try { return sessionStorage.getItem("iq-frontdesk-prompt") === "1"; } catch (_) { return false; } });
  const callEmp = state.employees.find((e) => e.id === callId);
  const timer = useRef(null);
  const liveRef = useRef({});
  const soundRef = useRef(soundOn);
  useEffect(() => { liveRef.current = live; }, [live]);
  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);

  const markPromptSeen = () => { setPromptSeen(true); try { sessionStorage.setItem("iq-frontdesk-prompt", "1"); } catch (_) {} };
  const routeTo = async (id, query = null) => {
    // If an associate is on a live phone call, ignore local voice/click requests
    // for them (they're busy on the phone) instead of starting a second session.
    // Re-check the live phone state right now: the 4s poll can lag behind a call
    // that just connected (e.g. Linda greeting an inbound caller), which would
    // otherwise let the front desk open a second web voice session — two voices.
    let busy = onCall.includes(id);
    if (!busy) {
      try {
        const d = await twilio.active();
        const live = d.on_call || [];
        setOnCall(live);
        busy = live.includes(id);
      } catch (_) {}
    }
    if (busy) {
      const nm = state.employees.find((e) => e.id === id)?.name || "That associate";
      toast.info(`${nm} is on a phone call right now — try again in a moment.`, { id: `busy-${id}` });
      return;
    }
    setCallQuery(query || null);
    setCallId(id);
  };
  const closeCall = () => { setCallId(null); setCallQuery(null); };
  const enterHQ = () => { unlockAudio(); setFdOn(true); try { sessionStorage.setItem("iq-frontdesk", "on"); } catch (_) {} markPromptSeen(); };
  const stopFrontDesk = () => { setFdOn(false); try { sessionStorage.setItem("iq-frontdesk", "off"); } catch (_) {} };
  const toggleFrontDesk = () => { if (fdOn) stopFrontDesk(); else enterHQ(); };

  const toggleSound = () => {
    setSoundOn((on) => {
      const next = !on;
      try { localStorage.setItem("iq-floor-sounds", next ? "1" : "0"); } catch (_) {}
      if (next) { unlockAudio(); startAmbience(); } else { stopAmbience(); }
      return next;
    });
  };

  // Start/stop the low-volume ambient office soundscape with the toggle, and
  // resume it automatically if the user had it on from a previous visit.
  useEffect(() => {
    if (soundOn) { unlockAudio(); startAmbience(); }
    return () => stopAmbience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (state.loaded && !state.briefingSeen) setBriefing(true); }, [state.loaded, state.briefingSeen]);

  // Live status sync (simulated): every few seconds an on-duty/on-task associate
  // flips between "On duty" (green) and "On a task" (red), so the floor feels alive.
  // Off-duty associates stay off. Overrides are transient (never persisted).
  useEffect(() => {
    if (!state.loaded) return;
    const active = state.employees.filter((e) => e.status !== "off_duty").map((e) => e.id);
    if (active.length === 0) return;
    timer.current = setInterval(() => {
      const id = active[Math.floor(Math.random() * active.length)];
      const base = state.employees.find((e) => e.id === id)?.status || "available";
      const cur = liveRef.current[id] !== undefined ? liveRef.current[id] : base;
      const wasTask = officeState(cur).key === "task";
      const next = wasTask ? "available" : "working";
      setLive((prev) => ({ ...prev, [id]: next }));
    }, 3500);
    return () => clearInterval(timer.current);
  }, [state.loaded, state.employees]);

  const statusOf = (e) => (onCall.includes(e.id) ? "on_call" : (live[e.id] !== undefined ? live[e.id] : e.status));
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  const wings = DEPARTMENTS
    .map((d) => ({ department: d, employees: state.employees.filter((e) => e.department === d.id) }))
    .filter((w) => w.employees.length > 0);

  return (
    <div>
      {briefing && <MorningBriefing onClose={() => setBriefing(false)} />}
      {callEmp && (
        <VoiceCall
          employee={callEmp}
          onClose={closeCall}
          associates={state.employees}
          onRoute={(id) => routeTo(id)}
          initialQuery={callQuery}
        />
      )}

      {/* First-load prompt to enter the voice front desk (needs one tap for mic). */}
      {!promptSeen && !callEmp && (
        <div className="fixed inset-0 z-[95] grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="frontdesk-prompt">
          <div className="absolute inset-0 bg-[#030A16]/80 backdrop-blur-md" onClick={markPromptSeen} />
          <motion.div
            className="glass-strong relative w-full max-w-sm rounded-3xl p-8 text-center"
            initial={{ scale: 0.92, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
          >
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-[#00E5FF]/10" style={{ boxShadow: "0 0 30px #00E5FF44" }}>
              <Radio size={28} className="text-[#00E5FF]" />
            </div>
            <h2 className="mt-5 font-display text-2xl font-semibold text-white">Enter the voice front desk</h2>
            <p className="mt-2 text-sm text-slate-400">Tap to go live, then just say an associate's name — Ava, Miles, Nora or Atlas — to start talking with them.</p>
            <button
              onClick={enterHQ}
              className="mt-6 w-full rounded-xl bg-[#00E5FF] px-4 py-3 text-sm font-semibold text-[#030A16] transition-transform hover:scale-[1.02]"
              data-testid="frontdesk-enter"
            >
              Tap to enter HQ
            </button>
            <button onClick={markPromptSeen} className="mt-3 text-xs text-slate-400 hover:text-white" data-testid="frontdesk-dismiss">
              Not now
            </button>
          </motion.div>
        </div>
      )}

      {/* Silent listening front desk — only while no call is open. */}
      {fdOn && !callEmp && (
        <FrontDesk employees={state.employees} onRoute={routeTo} onStop={stopFrontDesk} />
      )}

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">ABC Services</h1>
          <p className="mt-1 text-sm text-slate-400">Digital Headquarters · {today} · <span className="text-[#00E5FF]">Live activity</span> · {state.employees.length} associates</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4 text-[11px] text-slate-400" data-testid="office-legend">
            {Object.values(OFFICE).map((s) => (
              <span key={s.label} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color, boxShadow: `0 0 8px ${s.color}` }} />
                {s.label}
              </span>
            ))}
          </div>
          <button
            onClick={toggleFrontDesk}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors",
              fdOn ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-slate-400 hover:text-white"
            )}
            data-testid="frontdesk-toggle"
            title={fdOn ? "Front desk is listening" : "Turn on the voice front desk"}
            aria-pressed={fdOn}
          >
            <Radio size={14} />
            {fdOn ? "Front desk on" : "Front desk"}
          </button>
          <button
            onClick={toggleSound}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors",
              soundOn ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-slate-400 hover:text-white"
            )}
            data-testid="floor-sound-toggle"
            title={soundOn ? "Floor sounds on" : "Floor sounds off"}
            aria-pressed={soundOn}
          >
            {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            {soundOn ? "Sounds on" : "Sounds off"}
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-white/5 bg-[#050D1C]/40 bg-grid p-4 sm:p-6">
        <div className="grid grid-cols-1 justify-items-center gap-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {wings.map((w) => (
            <DepartmentWing key={w.department.id} department={w.department} employees={w.employees} statusOf={statusOf} onTalk={(id) => routeTo(id)} phoneByEmployee={noraPhone ? { linda: noraPhone } : {}} callStarts={callStarts} />
          ))}
        </div>
        <div className="mt-6 flex items-center gap-2 text-[11px] text-slate-500">
          <DoorClosed size={13} /> <span className="rounded-full border border-white/10 px-2 py-1">Tip: click any associate's office to start talking with them</span>
        </div>
      </div>
    </div>
  );
}
